import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  School, ShieldCheck, Users, ClipboardList, CalendarCheck, Printer,
  CheckCircle2, ArrowRight, BadgeCheck, Sparkles, BookOpen, Mic,
  Headphones, Brain, Radio, Ban, MessageCircle,
} from "lucide-react";
import { SHOW_PAYMENT } from "@/lib/paymentVisibility";

/* ---------------------------------------------------------------------------
 * PLACEHOLDERS — set these before the schools page goes public.
 * Prices are per student per year and must stay below the $100/yr family rate,
 * since bulk is the incentive. WALKTHROUGH_URL should become a real booking
 * link (Calendly or similar); until then it opens a pre-addressed email.
 * ------------------------------------------------------------------------- */
const SALES_EMAIL = "operations@hikmahverses.com";
const WALKTHROUGH_URL = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(
  "Book a 15-minute 600 Stories walkthrough"
)}&body=${encodeURIComponent(
  "School name:\nApprox. number of students:\nYear group(s):\nBest days/times for a 15-minute call:\n"
)}`;
const PILOT_URL = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(
  "Free one-classroom pilot — 600 Stories"
)}&body=${encodeURIComponent(
  "School name:\nClass size:\nYear group:\nPreferred start date:\n"
)}`;

const tiers = [
  { band: "25 – 99 students", price: "$40", note: "One class or a small year group", featured: false },
  { band: "100 – 299 students", price: "$30", note: "A full year group or small school", featured: true },
  { band: "300+ students", price: "$22", note: "Whole-school adoption — talk to us", featured: false },
];

const trustPoints = [
  {
    icon: Ban,
    title: "No Prophet depictions, ever",
    body: "Prophets are never drawn or described in appearance. They are referred to with the honorific 'peace be upon him'. This is a rule in the content pipeline, not a guideline.",
  },
  {
    icon: ShieldCheck,
    title: "No music, no ads, no open internet",
    body: "There is no instrumental music, no advertising of any kind, and no link that takes a child out of the app. Nothing is recommended by an engagement algorithm.",
  },
  {
    icon: MessageCircle,
    title: "The AI companion is fenced in",
    body: "Children can ask questions about the story they just finished. The assistant is restricted to age-appropriate Islamic answers, redirects sectarian or unsafe topics to a parent or teacher, and is capped at a fixed number of questions per child per day.",
  },
  {
    icon: Users,
    title: "Accounts are school-controlled",
    body: "Teacher and admin accounts are created by an administrator — they can never be self-assigned at signup. Students see only the stories their cohort has reached.",
  },
  {
    icon: ClipboardList,
    title: "Reporting a board can read",
    body: "Completion rates per class and the values reflected in student work — exportable. Not raw engagement metrics that mean nothing outside the product.",
  },
  {
    icon: BookOpen,
    title: "Source-referenced content",
    body: "Every story cites its Surah and ayah range. A teacher can check any lesson against the Mushaf in seconds.",
  },
];

const teacherWins = [
  { icon: ClipboardList, title: "Zero lesson prep", body: "A discussion guide is generated for every Sunday session." },
  { icon: CalendarCheck, title: "Walk in already knowing", body: "The readiness view shows who watched, who didn't, and who needs a nudge — before the class starts." },
  { icon: Users, title: "One-tap parent broadcast", body: "Replaces the weekly WhatsApp message a teacher would otherwise write by hand." },
  { icon: Sparkles, title: "Class pulse", body: "Percentage watched this week and the most-missed quiz question, at a glance." },
];

const pathway = [
  { icon: Sparkles, name: "Poems for Little Hearts", ages: "Ages 4–7", now: false },
  { icon: BookOpen, name: "600 Stories", ages: "Ages 8–13", now: true },
  { icon: Headphones, name: "Qur'an Fluency", ages: "Reading", now: false },
  { icon: Mic, name: "Tajweed", ages: "Recitation", now: false },
  { icon: Brain, name: "Hifz with Hikmah", ages: "Memorisation", now: false },
];

export default function Schools() {
  // React Router doesn't reset scroll on navigation, so a visitor who clicked
  // "For Schools" from partway down the homepage lands here at that same
  // pixel offset — mid-page, on an unrelated section. Smooth-scroll to the
  // top on arrival, same fix as the /families jump-to-pricing.
  useEffect(() => {
    const id = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <main className="hv-grain" data-testid="schools-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <span className="hv-badge" data-testid="schools-hero-badge">
            <School size={13} className="text-hv-teal" strokeWidth={3} /> For Schools &amp; Madrasahs
          </span>
          <h1 className="hv-heading mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="text-hv-navy">A Qur&apos;an curriculum your teachers can</span>{" "}
            <span className="text-hv-teal">run</span>{" "}
            <span className="text-hv-navy">— and your board can</span>{" "}
            <span className="text-hv-coral">approve</span>
            <span className="text-hv-navy">.</span>
          </h1>
          <p className="mt-6 text-hv-slate text-lg max-w-xl leading-relaxed">
            600 illustrated Qur&apos;an stories across 30 Juz, delivered two a week on a rhythm a class
            can actually keep. Start with one classroom, one term, free.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={WALKTHROUGH_URL} data-testid="cta-walkthrough" className="hv-btn-primary inline-flex items-center gap-2">
              <CalendarCheck size={18} strokeWidth={2.5} /> Book a 15-minute walkthrough
            </a>
            <a href="#trust" data-testid="cta-trust-onepager" className="hv-btn-secondary inline-flex items-center gap-2">
              <ShieldCheck size={18} strokeWidth={2.5} /> Read the trust one-pager
            </a>
          </div>
          <p className="mt-5 text-hv-slate text-sm">
            No card, no contract to look. Invoice and PO billing available.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="hv-card p-8" data-testid="pilot-card">
            <span className="hv-badge hv-badge-coral">Free pilot</span>
            <h2 className="hv-heading mt-4 text-2xl text-hv-navy">One classroom. One term. No cost.</h2>
            <ul className="mt-6 space-y-3">
              {[
                "Up to 35 students, fully licensed",
                "Teacher account + roster import done for you",
                "Weekly discussion guides included",
                "A completion report at the end of term",
                "No auto-renewal, no card on file",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-hv-navy" data-testid={`pilot-feature-${i}`}>
                  <CheckCircle2 size={16} className="text-hv-teal mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a href={PILOT_URL} data-testid="cta-pilot" className="hv-btn-primary mt-7 w-full inline-flex items-center justify-center gap-2">
              Request the free pilot <ArrowRight size={18} strokeWidth={2.5} />
            </a>
            {/* Schools ready to go straight to setup skip the email entirely. */}
            <Link to="/schools/onboarding" data-testid="cta-onboarding"
                  className="hv-btn-secondary mt-3 w-full inline-flex items-center justify-center gap-2">
              <ClipboardList size={18} strokeWidth={2.5} /> Send us your roster
            </Link>
          </div>
        </div>
      </section>

      {/* TEACHER WINS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <span className="hv-badge">For the teacher</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Less prep than</span>{" "}
            <span className="text-hv-coral">not using it.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">
            The programme has to save a teacher time in week one, or it will not survive week three.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {teacherWins.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} className="hv-card p-7" data-testid={`teacher-win-${i}`}>
                <div className="hv-icon-circle w-11 h-11 rounded-full flex items-center justify-center">
                  <Icon size={20} strokeWidth={2.5} />
                </div>
                <h3 className="hv-heading mt-4 text-lg text-hv-navy">{w.title}</h3>
                <p className="mt-2 text-hv-slate text-sm leading-relaxed">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TRUST ONE-PAGER */}
      <section id="trust" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="hv-card p-8 sm:p-12" data-testid="trust-onepager">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <span className="hv-badge">Trust one-pager</span>
              <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
                <span className="text-hv-navy">What we do with</span>{" "}
                <span className="text-hv-teal">your students.</span>
              </h2>
              <p className="mt-3 text-hv-slate leading-relaxed">
                Written in plain language, so it can be forwarded to a board without translation.
              </p>
            </div>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="print-trust"
              className="hv-btn-secondary inline-flex items-center gap-2 shrink-0"
            >
              <Printer size={18} strokeWidth={2.5} /> Print / save as PDF
            </button>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-8">
            {trustPoints.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex gap-4" data-testid={`trust-point-${i}`}>
                  <div className="hv-icon-circle-coral w-11 h-11 rounded-full flex items-center justify-center shrink-0">
                    <Icon size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="hv-heading text-lg text-hv-navy">{p.title}</h3>
                    <p className="mt-1.5 text-hv-slate text-sm leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {SHOW_PAYMENT && (
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <span className="hv-badge">Institutional pricing</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Per student, per year —</span>{" "}
            <span className="text-hv-coral">always below the family rate.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">
            Families pay $100 a year for one child. Schools pay less per student, and less again at scale.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-3 gap-5 items-stretch" data-testid="school-pricing-grid">
          {tiers.map((t, i) => (
            <div
              key={i}
              data-testid={`school-tier-${i}`}
              className={`hv-card p-8 flex flex-col ${
                t.featured ? "bg-hv-navy text-white ring-4 ring-hv-coral/40 relative overflow-hidden" : ""
              }`}
            >
              {t.featured && <span className="absolute top-4 end-4 hv-badge hv-badge-coral">Most schools</span>}
              <div className={`text-sm font-bold ${t.featured ? "text-white/80" : "text-hv-slate"}`}>{t.band}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className={`hv-heading text-5xl ${t.featured ? "text-white" : "text-hv-navy"}`}>{t.price}</div>
                <div className={t.featured ? "text-white/70" : "text-hv-slate"}>/ student / year</div>
              </div>
              <p className={`mt-3 text-sm ${t.featured ? "text-white/80" : "text-hv-slate"}`}>{t.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={WALKTHROUGH_URL} data-testid="pricing-walkthrough" className="hv-btn-primary inline-flex items-center gap-2">
            <CalendarCheck size={18} strokeWidth={2.5} /> Book a walkthrough
          </a>
          <a href={PILOT_URL} data-testid="pricing-pilot" className="hv-btn-secondary inline-flex items-center gap-2">
            Start with a free pilot
          </a>
        </div>
        <p className="mt-5 text-center text-hv-slate text-sm">
          Invoice / PO billing · net-30
        </p>

        {/* Bundling is what turns a lead magnet into ecosystem revenue. */}
        <div className="mt-10 grid sm:grid-cols-3 gap-5" data-testid="bundle-pricing">
          {[
            { n: "1 programme", off: "—", note: "Standard per-student rate above" },
            { n: "2 programmes", off: "15% off", note: "e.g. 600 Stories + Tajweed" },
            { n: "3 or more", off: "25% off", note: "The full cradle-to-grave pathway" },
          ].map((b, i) => (
            <div key={i} data-testid={`bundle-${i}`}
                 className="rounded-2xl border-2 border-[#E5DFC8] bg-white p-6 text-center">
              <div className="text-sm font-bold text-hv-slate">{b.n}</div>
              <div className="hv-heading text-3xl text-hv-teal mt-2">{b.off}</div>
              <div className="text-xs text-hv-slate mt-2">{b.note}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ECOSYSTEM PATHWAY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <span className="hv-badge">The pathway</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Not six vendors.</span>{" "}
            <span className="text-hv-teal">One curriculum, in stages.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">
            600 Stories is the widest door in. Schools typically add the next stage once a cohort ages up.
          </p>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pathway">
          {pathway.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`pathway-${i}`}
                className={`hv-card p-6 flex flex-col items-start ${s.now ? "ring-4 ring-hv-teal/30" : ""}`}
              >
                <div className={`${s.now ? "hv-icon-circle-coral" : "hv-icon-circle"} w-11 h-11 rounded-full flex items-center justify-center`}>
                  <Icon size={20} strokeWidth={2.5} />
                </div>
                {s.now && <span className="hv-badge hv-badge-coral mt-3">You are here</span>}
                <h3 className="hv-heading mt-3 text-base text-hv-navy leading-snug">{s.name}</h3>
                <p className="mt-1 text-hv-slate text-sm">{s.ages}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SCHOOL MARKETING BADGE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="hv-card p-8 sm:p-12 grid lg:grid-cols-12 gap-10 items-center" data-testid="school-badge">
          <div className="lg:col-span-7">
            <span className="hv-badge">Use it in your prospectus</span>
            <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
              <span className="text-hv-navy">A</span>{" "}
              <span className="text-hv-coral">Trusted 600 Stories School</span>{" "}
              <span className="text-hv-navy">badge.</span>
            </h2>
            <p className="mt-4 text-hv-slate leading-relaxed max-w-xl">
              Every partner school gets a badge for its own website, prospectus and open-day material —
              so adopting the programme helps you recruit families, not just fill a curriculum slot.
            </p>
          </div>
          <div className="lg:col-span-5 flex justify-center">
            <div className="rounded-3xl bg-[#FBF6E4] border-2 border-[#E5DFC8] px-8 py-10 text-center max-w-xs">
              <div className="hv-icon-circle-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                <BadgeCheck size={30} strokeWidth={2.5} />
              </div>
              <div className="hv-heading text-hv-navy text-xl mt-4 leading-tight">Trusted 600 Stories School</div>
              <div className="text-hv-teal text-xs font-bold uppercase tracking-[0.2em] mt-2">HikmahVerses</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="hv-card p-10 sm:p-14 text-center" data-testid="schools-final-cta">
          <span className="hv-badge">Next step</span>
          <h2 className="hv-heading mt-5 text-3xl sm:text-4xl lg:text-5xl">
            <span className="text-hv-navy">See it with your own</span>{" "}
            <span className="text-hv-teal">class list.</span>
          </h2>
          <p className="mt-4 text-hv-slate max-w-xl mx-auto leading-relaxed">
            Fifteen minutes, a real walkthrough, and the trust one-pager to take to your board.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href={WALKTHROUGH_URL} data-testid="schools-final-primary" className="hv-btn-primary inline-flex items-center gap-2">
              <CalendarCheck size={18} strokeWidth={2.5} /> Book a walkthrough
            </a>
            <Link to="/families" data-testid="schools-to-families" className="hv-btn-secondary inline-flex items-center gap-2">
              <Radio size={18} strokeWidth={2.5} /> I&apos;m a parent, not a school
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-5 sm:px-8 py-8 text-center text-hv-slate text-sm">
        HikmahVerses · 600 stories · 30 Juz · one journey.
      </footer>
    </main>
  );
}
